import React, { useState } from 'react';
import {
  TrendingUp,
  DollarSign,
  Info,
  Sliders,
  ShieldCheck,
  CheckCircle2,
  Calendar,
  Layers,
  ArrowRight,
  ChevronDown,
  Sparkles,
  BookOpen,
} from 'lucide-react';
import { WealthCalculationInputs, CompoundingFrequency, ContributionFrequency, ContributionTiming } from '../../types';
import { calculateCompoundGrowth } from '../../lib/wealthMathEngine';

export const SavingsCompoundCalculatorView: React.FC = () => {
  const [inputs, setInputs] = useState<WealthCalculationInputs>({
    initialPrincipal: 10000,
    periodicContribution: 500,
    nominalReturnRatePct: 7.0,
    inflationRatePct: 2.5,
    annualFeePct: 0.25,
    timeHorizonYears: 20,
    compoundingFrequency: 'monthly',
    contributionFrequency: 'monthly',
    contributionTiming: 'END_OF_PERIOD',
    annualContributionIncreasePct: 0,
    currency: 'USD',
  });

  const [showFormulaTrace, setShowFormulaTrace] = useState(false);
  const [showInRealMoney, setShowInRealMoney] = useState(true);

  const result = calculateCompoundGrowth(inputs);

  const handleInputChange = (field: keyof WealthCalculationInputs, value: any) => {
    setInputs((prev) => ({
      ...prev,
      [field]: typeof value === 'number' && isNaN(value) ? 0 : value,
    }));
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-16">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-emerald-900 via-teal-900 to-slate-900 rounded-2xl p-6 sm:p-8 text-white shadow-xl">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-semibold border border-emerald-500/30">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>YamooPay Universal Wealth Core</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              Global Savings & Compound Interest Intelligence
            </h1>
            <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
              Deterministic, explainable compound growth calculations with multiplicative real inflation adjustment, fee impact analysis, and formula step verification.
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20 text-right space-y-1">
            <div className="text-xs text-slate-300 font-medium">Estimated Future Value ({inputs.currency})</div>
            <div className="text-2xl sm:text-3xl font-extrabold text-emerald-400">
              ${(showInRealMoney ? result.realFutureValue : result.nominalFutureValue).toLocaleString(undefined, {
                maximumFractionDigits: 0,
              })}
            </div>
            <div className="text-[11px] text-slate-300">
              {showInRealMoney ? "In today's purchasing power" : 'In future nominal value'}
            </div>
          </div>
        </div>
      </div>

      {/* Trust & Non-Advice Disclaimer */}
      <div className="bg-amber-50/80 border border-amber-200 rounded-xl p-4 text-xs text-amber-900 flex items-start gap-3">
        <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="leading-relaxed">
          <span className="font-semibold">Illustrative Scenario Disclaimer:</span> YamooPay provides deterministic financial calculations based strictly on your user-defined parameters. Future returns and inflation rates are uncertain and differ materially from past performance. This tool does not constitute financial or investment advice.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Controls Column */}
        <div className="lg:col-span-5 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-6">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4">
            <div className="flex items-center gap-2">
              <Sliders className="w-5 h-5 text-emerald-600" />
              <h2 className="text-base font-bold text-slate-900">Scenario Parameters</h2>
            </div>
            <span className="text-xs text-slate-500 font-mono">Formula: {result.formulaVersion}</span>
          </div>

          <div className="space-y-4 text-xs text-slate-700">
            {/* Initial Principal */}
            <div>
              <label className="block font-semibold text-slate-900 mb-1">
                Initial Principal Deposit (${inputs.currency})
              </label>
              <div className="relative">
                <DollarSign className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                <input
                  type="number"
                  min="0"
                  value={inputs.initialPrincipal}
                  onChange={(e) => handleInputChange('initialPrincipal', parseFloat(e.target.value))}
                  className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            {/* Periodic Contribution */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="font-semibold text-slate-900">
                  Recurring Contribution (${inputs.currency})
                </label>
                <select
                  value={inputs.contributionFrequency}
                  onChange={(e) => handleInputChange('contributionFrequency', e.target.value as ContributionFrequency)}
                  className="bg-slate-100 text-slate-700 text-[11px] font-semibold rounded px-2 py-0.5 border border-slate-200"
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
              <div className="relative">
                <DollarSign className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                <input
                  type="number"
                  min="0"
                  value={inputs.periodicContribution}
                  onChange={(e) => handleInputChange('periodicContribution', parseFloat(e.target.value))}
                  className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            {/* Nominal Return & Inflation */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Assumed Return (%/yr)</label>
                <input
                  type="number"
                  step="0.1"
                  value={inputs.nominalReturnRatePct}
                  onChange={(e) => handleInputChange('nominalReturnRatePct', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Assumed Inflation (%/yr)</label>
                <input
                  type="number"
                  step="0.1"
                  value={inputs.inflationRatePct}
                  onChange={(e) => handleInputChange('inflationRatePct', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            {/* Fees & Horizon */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Annual Fee Ratio (%)</label>
                <input
                  type="number"
                  step="0.05"
                  value={inputs.annualFeePct}
                  onChange={(e) => handleInputChange('annualFeePct', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Time Horizon (Years)</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={inputs.timeHorizonYears}
                  onChange={(e) => handleInputChange('timeHorizonYears', parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            {/* Timing & Compounding Frequency */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Compounding</label>
                <select
                  value={inputs.compoundingFrequency}
                  onChange={(e) => handleInputChange('compoundingFrequency', e.target.value as CompoundingFrequency)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="daily">Daily</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annually</option>
                </select>
              </div>
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Contribution Timing</label>
                <select
                  value={inputs.contributionTiming}
                  onChange={(e) => handleInputChange('contributionTiming', e.target.value as ContributionTiming)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="END_OF_PERIOD">End of Period</option>
                  <option value="BEGINNING_OF_PERIOD">Beginning of Period</option>
                </select>
              </div>
            </div>

            {/* Annual Step-up */}
            <div className="pt-2 border-t border-slate-100">
              <label className="block font-semibold text-slate-900 mb-1">Annual Contribution Step-Up (%)</label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={inputs.annualContributionIncreasePct || 0}
                onChange={(e) => handleInputChange('annualContributionIncreasePct', parseFloat(e.target.value))}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <span className="text-[10px] text-slate-500">
                Models increasing contributions annually with salary growth or savings rate upgrades.
              </span>
            </div>
          </div>
        </div>

        {/* Results & Visual Analytics Column */}
        <div className="lg:col-span-7 space-y-6">
          {/* Main KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-2">
              <div className="text-xs text-slate-500 font-medium">Total Principal & Deposits</div>
              <div className="text-xl font-bold text-slate-900">
                ${result.totalContributions.toLocaleString()}
              </div>
              <div className="text-[11px] text-slate-500">
                ${inputs.initialPrincipal.toLocaleString()} initial + ${(result.totalContributions - inputs.initialPrincipal).toLocaleString()} recurring
              </div>
            </div>

            <div className="bg-emerald-50/50 border border-emerald-200 rounded-2xl p-5 shadow-sm space-y-2">
              <div className="text-xs text-emerald-800 font-semibold">Total Projected Growth</div>
              <div className="text-xl font-bold text-emerald-900">
                +${result.totalGrowth.toLocaleString()}
              </div>
              <div className="text-[11px] text-emerald-700">
                Compound growth contribution
              </div>
            </div>

            <div className="bg-slate-900 text-white rounded-2xl p-5 shadow-sm space-y-2">
              <div className="text-xs text-slate-300 font-medium">Effective Real Return</div>
              <div className="text-xl font-bold text-emerald-400">
                {result.effectiveRealReturnPct}%
              </div>
              <div className="text-[11px] text-slate-400">
                After {inputs.inflationRatePct}% inflation & {inputs.annualFeePct}% fee
              </div>
            </div>
          </div>

          {/* Breakdown & Inflation Toggle */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-6">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div>
                <h3 className="text-base font-bold text-slate-900">Growth Breakdown & Purchasing Power</h3>
                <p className="text-xs text-slate-500">Visual breakdown of principal versus compound interest earnings</p>
              </div>

              <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
                <button
                  onClick={() => setShowInRealMoney(false)}
                  className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors cursor-pointer ${
                    !showInRealMoney ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Nominal
                </button>
                <button
                  onClick={() => setShowInRealMoney(true)}
                  className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors cursor-pointer ${
                    showInRealMoney ? 'bg-emerald-600 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Today's Money (Real)
                </button>
              </div>
            </div>

            {/* Stacked Growth Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-xs font-semibold text-slate-700">
                <span>Deposits: ${result.totalContributions.toLocaleString()} ({Math.round((result.totalContributions / result.nominalFutureValue) * 100)}%)</span>
                <span className="text-emerald-700">Compound Growth: ${result.totalGrowth.toLocaleString()} ({Math.round((result.totalGrowth / result.nominalFutureValue) * 100)}%)</span>
              </div>
              <div className="w-full h-4 bg-slate-100 rounded-full overflow-hidden flex">
                <div
                  style={{ width: `${(result.totalContributions / result.nominalFutureValue) * 100}%` }}
                  className="bg-slate-700 h-full transition-all"
                  title="Total Contributions"
                />
                <div
                  style={{ width: `${(result.totalGrowth / result.nominalFutureValue) * 100}%` }}
                  className="bg-emerald-500 h-full transition-all"
                  title="Compound Growth"
                />
              </div>
            </div>

            {/* Fee Warning */}
            {inputs.annualFeePct > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center justify-between text-xs">
                <div className="space-y-0.5">
                  <div className="font-semibold text-slate-900">Management Fee Impact ({inputs.annualFeePct}%)</div>
                  <div className="text-slate-600">
                    Total direct fees: <span className="font-semibold text-slate-900">${result.totalFeesDeducted.toLocaleString()}</span>. Lost potential growth due to fees: <span className="font-semibold text-rose-600">${result.lostGrowthDueToFees.toLocaleString()}</span>
                  </div>
                </div>
                <BookOpen className="w-5 h-5 text-slate-400 shrink-0" />
              </div>
            )}

            {/* Yearly Table Preview */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Yearly Trajectory Schedule</h4>
                <button
                  onClick={() => setShowFormulaTrace(!showFormulaTrace)}
                  className="text-xs text-emerald-700 hover:text-emerald-900 font-semibold flex items-center gap-1 cursor-pointer"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>{showFormulaTrace ? 'Hide Math Verification' : 'Verify Math Step-by-Step'}</span>
                </button>
              </div>

              <div className="border border-slate-200 rounded-xl overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200">
                    <tr>
                      <th className="py-2.5 px-3">Year</th>
                      <th className="py-2.5 px-3">Deposits Sum</th>
                      <th className="py-2.5 px-3">Interest Earned</th>
                      <th className="py-2.5 px-3">Ending (Nominal)</th>
                      <th className="py-2.5 px-3 text-right">Ending (Real Money)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-800">
                    {result.yearlyBreakdown.filter((_, idx) => idx < 5 || idx === result.yearlyBreakdown.length - 1).map((row) => (
                      <tr key={row.year} className="hover:bg-slate-50">
                        <td className="py-2.5 px-3 font-semibold text-slate-900">Year {row.year}</td>
                        <td className="py-2.5 px-3">${row.totalContributionsNominal.toLocaleString()}</td>
                        <td className="py-2.5 px-3 text-emerald-700 font-medium">+${row.interestEarnedNominal.toLocaleString()}</td>
                        <td className="py-2.5 px-3 font-semibold text-slate-900">${row.endingBalanceNominal.toLocaleString()}</td>
                        <td className="py-2.5 px-3 font-bold text-emerald-900 text-right">${row.endingBalanceReal.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Formula Trace Modal / Collapsible */}
            {showFormulaTrace && (
              <div className="bg-slate-900 text-slate-200 rounded-xl p-5 space-y-4 text-xs font-mono border border-slate-800">
                <div className="flex items-center justify-between border-b border-slate-800 pb-3 text-emerald-400 font-sans font-bold">
                  <span className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4" />
                    Deterministic Calculation Trace — YamooPay Core Math
                  </span>
                  <span>Version: {result.formulaVersion}</span>
                </div>
                <div className="space-y-3">
                  {result.formulaTrace.map((step, idx) => (
                    <div key={idx} className="space-y-1 bg-slate-950 p-3 rounded-lg border border-slate-800">
                      <div className="text-slate-400 font-sans font-semibold">{step.stepName}</div>
                      <div className="text-amber-300">{step.formulaText}</div>
                      <div className="text-emerald-300 font-bold">{step.calculatedValue}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
